import React, { useState } from "react";
import makeStyles from "@mui/styles/makeStyles";
import PropTypes from "prop-types";
import { Box, Button } from "@mui/material";
import {
  ChooseFileField,
  LinkField,
  NameField,
  DescriptionField,
  TagsField,
  CommentsField,
  DurationFieldHours,
  DurationFieldMinutes,
  IsQuizImportField,
  QuizOrSurveyField,
  QuizInstructionsField,
  RandomlyOrderedQuestionsField,
  NumberOfServedQuestionsField,
  SurveyAnonymousReportingField,
  QuestionsPerPageField,
  newContentFormFields,
  EditorBehaviorField
} from "./NewContentFields";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useTranslation } from "react-i18next";
import {
  validateHttpsUrl,
  fileUploadValidation,
  quizImportFileFormatValidation
} from "../../utils/validation";
import { Grid } from "@mui/material";
import { editorInlineItemsBehavior } from "../../constants";

const useStyles = makeStyles(theme => ({
  button: {
    marginLeft: theme.spacing(2)
  }
}));

export const NewContentForm = ({
  submitForm,
  onCancel,
  onButtonClick,
  isSubmitting,
  values,
  setFieldValue,
  allowQuizImport,
  buttonTypes,
  showEditorBehaviorField
}) => {
  const { t } = useTranslation();
  const isQuizOrSurvey = values ? values.isQuizImport : false;
  return (
    <Form>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <ChooseFileField
            multiple={false}
            linkedField={newContentFormFields.name}
          />
          {allowQuizImport && <IsQuizImportField />}
        </Grid>
        {!isQuizOrSurvey && (
          <Grid item xs={12}>
            <LinkField />
          </Grid>
        )}
        {isQuizOrSurvey && (
          <Grid item xs={12}>
            <QuizOrSurveyField />
          </Grid>
        )}
        <Grid item xs={12}>
          <NameField
            label={t("addNewContent.addItem.name")}
            helperText={t("addNewContent.requiredHelperText")}
          />
        </Grid>
        {!isQuizOrSurvey && (
          <>
            <Grid item xs={12}>
              <DescriptionField />
            </Grid>
            <Grid item xs={12} md={6}>
              <DurationFieldHours />
            </Grid>
            <Grid item xs={12} md={6}>
              <DurationFieldMinutes />
            </Grid>
            <Grid item xs={12}>
              <TagsField />
            </Grid>
            <Grid item xs={12}>
              <CommentsField />
            </Grid>
          </>
        )}
        {showEditorBehaviorField && (
          <Grid item xs={12}>
            <EditorBehaviorField />
          </Grid>
        )}
        {isQuizOrSurvey && (
          <>
            <Grid item xs={12}>
              <QuizInstructionsField
                setFieldValue={setFieldValue}
                values={values}
              />
            </Grid>
            <Grid container>
              <Grid container item xs={12} md={6}>
                <Grid item xs={12}>
                  <RandomlyOrderedQuestionsField
                    type={values[newContentFormFields.quizOrSurvey]}
                  />
                </Grid>
                <Grid item xs={12}>
                  <NumberOfServedQuestionsField
                    disabled={!values.randomlyOrdered}
                  />
                </Grid>
              </Grid>
              <Grid container item xs={12} md={6}>
                <Grid item xs={12}>
                  <QuestionsPerPageField />
                </Grid>
                {values[newContentFormFields.quizOrSurvey] === "survey" && (
                  <Grid item xs={12}>
                    <SurveyAnonymousReportingField />
                  </Grid>
                )}
              </Grid>
            </Grid>
          </>
        )}
      </Grid>
      <Box display="flex" mt={4.5} justifyContent="flex-end">
        {buttonTypes &&
          buttonTypes.map(buttonType => {
            return (
              <AddNewContentFormButton
                key={buttonType}
                submitForm={() => submitForm()}
                onButtonClick={() => onButtonClick(buttonType)}
                buttonType={buttonType}
                isSubmitting={isSubmitting}
                onCancel={onCancel}
              />
            );
          })}
      </Box>
    </Form>
  );
};

const AddNewContentFormButton = ({
  buttonType,
  isSubmitting,
  onButtonClick,
  onCancel,
  submitForm
}) => {
  const classes = useStyles();
  const { t } = useTranslation();

  if (buttonType === "submit") {
    return (
      <Button
        className={classes.button}
        variant="contained"
        color="primary"
        disabled={isSubmitting}
        onClick={submitForm}
      >
        {t("addNewContent.addItem.submit")}
      </Button>
    );
  } else if (buttonType === "submitAndNew") {
    return (
      <Button
        className={classes.button}
        variant="contained"
        color="primary"
        disabled={isSubmitting}
        onClick={() => {
          onButtonClick();
          submitForm();
        }}
      >
        {t("addNewContent.addItem.submitAndNew")}
      </Button>
    );
  } else if (buttonType === "cancel") {
    return (
      <Button color="primary" onClick={onCancel} className={classes.button}>
        {t("addNewContent.addItem.cancel")}
      </Button>
    );
  } else {
    return null;
  }
};

export const initialValues = {
  [newContentFormFields.file]: null,
  [newContentFormFields.link]: "",
  [newContentFormFields.name]: "",
  [newContentFormFields.description]: "",
  [newContentFormFields.durationHours]: 0,
  [newContentFormFields.durationMinutes]: "0",
  [newContentFormFields.tags]: [],
  [newContentFormFields.comment]: "",
  [newContentFormFields.isQuizImport]: false,
  [newContentFormFields.quizOrSurvey]: "quiz",
  [newContentFormFields.quizInstructions]: "",
  [newContentFormFields.randomlyOrdered]: false,
  [newContentFormFields.numQuestions]: "",
  [newContentFormFields.anonymousReporting]: false,
  [newContentFormFields.questionsPerPage]: "all",
  [newContentFormFields.editorBehavior]: editorInlineItemsBehavior.openInNewTab
};

export const getValidationSchema = () => {
  const { t } = useTranslation();
  return Yup.object().shape({
    name: Yup.string()
      .required(t("validationMessages.nameRequired"))
      .max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.name"),
          maxLength: 255
        })
      ),
    [newContentFormFields.file]: fileUploadValidation(t).test(
      "fileRequired",
      t("validationMessages.fileOrLinkRequired"),
      function() {
        return !!this.parent[newContentFormFields.file]
          ? !this.parent[newContentFormFields.link]
          : !!this.parent[newContentFormFields.link];
      }
    ),
    [newContentFormFields.link]: Yup.mixed()
      .test(
        "linkRequired",
        t("validationMessages.fileOrLinkRequired"),
        function() {
          return !!this.parent[newContentFormFields.file]
            ? !this.parent[newContentFormFields.link]
            : !!this.parent[newContentFormFields.link];
        }
      )
      .test(
        "validUrl",
        t("validationMessages.validHttpsUrl"),
        link => !link || validateHttpsUrl(link)
      ),
    [newContentFormFields.isQuizImport]: Yup.boolean().test(
      "validFileFormats",
      t("validationMessages.validQuizImportFileFormats"),
      function() {
        return (
          !this.parent[newContentFormFields.isQuizImport] ||
          quizImportFileFormatValidation(this.parent[newContentFormFields.file])
        );
      }
    ),
    [newContentFormFields.durationHours]: Yup.number().min(
      0,
      t("validationMessages.validNumber")
    ),
    [newContentFormFields.description]: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("addNewContent.addItem.description"),
        maxLength: 350
      })
    ),
    [newContentFormFields.comment]: Yup.string().max(
      350,
      t("validationMessages.cannotExceedLength", {
        type: t("addNewContent.addItem.comments"),
        maxLength: 350
      })
    ),
    [newContentFormFields.tags]: Yup.array().of(
      Yup.string().max(
        255,
        t("validationMessages.cannotExceedLength", {
          type: t("addNewContent.addItem.tags"),
          maxLength: 255
        })
      )
    )
  });
};

const AddNewContentForm = ({
  onSubmit,
  onSubmitAndNew,
  onCancel,
  buttonTypes,
  allowQuizImport,
  showEditorBehaviorField
}) => {
  const [submitType, setSubmitType] = useState("");
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={getValidationSchema()}
      onSubmit={(values, { setSubmitting }) => {
        setSubmitting(true);
        if (submitType === "submitAndNew") {
          onSubmitAndNew(values, () => setSubmitting(false));
        } else {
          onSubmit(values, () => setSubmitting(false));
        }
      }}
    >
      {({ submitForm, isSubmitting, values, setFieldValue }) => (
        <NewContentForm
          isSubmitting={isSubmitting}
          buttonTypes={buttonTypes}
          onCancel={onCancel}
          submitForm={submitForm}
          onButtonClick={buttonType => setSubmitType(buttonType)}
          values={values}
          setFieldValue={setFieldValue}
          allowQuizImport={allowQuizImport}
          showEditorBehaviorField={showEditorBehaviorField}
        />
      )}
    </Formik>
  );
};
export default AddNewContentForm;

AddNewContentForm.propTypes = {
  /**
   * Called when the form is submitted
   */
  onSubmit: PropTypes.func,
  /**
   * Called when submit and new button is clicked
   */
  onSubmitAndNew: PropTypes.func,
  /**
   * Called when the cancel button is clicked
   */
  onCancel: PropTypes.func,
  /**
   * The buttons that will be displayed in the form
   * Possible values are "submit", "cancel", and "submitAndNew"
   */
  buttonTypes: PropTypes.arrayOf(PropTypes.string).isRequired,
  showEditorBehaviorField: PropTypes.bool
};

AddNewContentForm.defaultProps = {
  allowQuizImport: true
};
